import React, { useState, useRef, useEffect } from 'react';
import { useAppContext } from '../AppContext';
import LoadingSpinner from './LoadingSpinner';
import * as LucideIcons from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { httpsCallable } from 'firebase/functions';

const EditProfileModal = ({ isOpen, onClose, currentUserProfile, onSave }) => {
    const { t } = useTranslation();
    const { appFunctions, showConfirmation, TOKEN_COSTS, setMessage } = useAppContext();
    const [displayName, setDisplayName] = useState(currentUserProfile?.displayName || '');
    const [bio, setBio] = useState(currentUserProfile?.bio || '');
    const [interests, setInterests] = useState(currentUserProfile?.interests?.join(', ') || '');
    const [accentColor, setAccentColor] = useState(currentUserProfile?.accentColor || '#FFFFFF');
    const [headerImageFile, setHeaderImageFile] = useState(null);
    const [headerPreview, setHeaderPreview] = useState(currentUserProfile?.headerImageURL || '');
    const [avatarFile, setAvatarFile] = useState(null);
    const [avatarPreview, setAvatarPreview] = useState(currentUserProfile?.photoURL || '');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [isEnhancingBio, setIsEnhancingBio] = useState(false);
    const [bioSuggestions, setBioSuggestions] = useState([]);
    const [showBioSuggestions, setShowBioSuggestions] = useState(false);
    const [selectedVibe, setSelectedVibe] = useState('Standard');

    const headerInputRef = useRef(null);
    const avatarInputRef = useRef(null);

    const vibes = ['Standard', 'Witty', 'Mysterious', 'Poetic', 'Professional', 'Friendly'];

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen && currentUserProfile) {
            setDisplayName(currentUserProfile.displayName || '');
            setBio(currentUserProfile.bio || '');
            setInterests(currentUserProfile.interests?.join(', ') || '');
            setAccentColor(currentUserProfile.accentColor || '#FFFFFF');
            setHeaderPreview(currentUserProfile.headerImageURL || '');
            setAvatarPreview(currentUserProfile.photoURL || '');
            setHeaderImageFile(null);
            setAvatarFile(null);
            setError('');
            setBioSuggestions([]);
            setShowBioSuggestions(false);
            setSelectedVibe('Standard');
        }
    }, [isOpen, currentUserProfile]);

    const handleEnhanceBio = async () => {
        if (!bio) return;
        setIsEnhancingBio(true);
        try {
            const enhanceBioFn = httpsCallable(appFunctions, 'enhanceBio');
            const result = await enhanceBioFn({ currentBio: bio, vibe: selectedVibe });
            if (result.data.suggestions?.length > 0) {
                setBioSuggestions(result.data.suggestions);
                setShowBioSuggestions(true);
            } else {
                setMessage(t('userProfile.strings.noSuggestions') || "No suggestions generated.");
            }
        } catch (err) {
            console.error(err);
            setError(t('userProfile.errors.enhanceBio') || "Failed to enhance bio.");
        } finally {
            setIsEnhancingBio(false);
        }
    };

    const confirmEnhanceBio = () => {
        showConfirmation({
            message: t('userProfile.enhanceBioConfirmation', { cost: TOKEN_COSTS?.BIO_ENHANCER || 15 }) || "Enhance bio with AI? This will cost tokens.",
            onConfirm: handleEnhanceBio
        });
    };

    const handleHeaderChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setHeaderImageFile(file);
            setHeaderPreview(URL.createObjectURL(file));
        }
    };

    const handleAvatarChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setAvatarFile(file);
            setAvatarPreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async () => {
        if (!displayName.trim()) {
            setError(t('userProfile.validation.displayNameRequired') || "Display name is required");
            return;
        }

        setIsSaving(true);
        setError('');

        try {
            await onSave({
                displayName,
                bio,
                interests: interests.split(',').map(i => i.trim()).filter(Boolean),
                accentColor,
                headerImageFile,
                avatarFile
            });
            onClose();
        } catch (err) {
            setError(err.message || t('userProfile.errors.updateFailed') || "Failed to update profile");
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn">
            <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
                
                {/* Header */}
                <div className="p-4 border-b border-gray-800 flex justify-between items-center bg-gray-800/50">
                    <h2 className="text-xl font-bold text-white font-playfair flex items-center gap-2">
                        <LucideIcons.Edit3 size={20} className="text-blue-400" />
                        {t('userProfile.actions.editProfile')}
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
                        <LucideIcons.X size={24} />
                    </button>
                </div>

                {/* Body - Scrollable */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
                    
                    {error && (
                        <div className="p-3 bg-red-900/30 border border-red-500/50 rounded-lg text-red-200 text-sm flex items-center gap-2">
                            <LucideIcons.AlertCircle size={16} />
                            {error}
                        </div>
                    )}

                    {/* Visuals Section */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">{t('userProfile.edit.visuals') || "Visuals"}</h3>
                        
                        {/* Header Image */}
                        <div className="relative h-32 w-full rounded-lg overflow-hidden border border-gray-700 group bg-gray-800">
                            {headerPreview ? (
                                <img src={headerPreview} alt="Header" className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-500">
                                    <LucideIcons.Image size={32} />
                                </div>
                            )}
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer" onClick={() => headerInputRef.current.click()}>
                                <span className="text-white font-medium flex items-center gap-2">
                                    <LucideIcons.Upload size={18} /> {t('userProfile.edit.changeHeader') || "Change Header"}
                                </span>
                            </div>
                            <input 
                                type="file" 
                                ref={headerInputRef} 
                                onChange={handleHeaderChange} 
                                className="hidden" 
                                accept="image/png, image/jpeg, image/gif, image/webp" 
                            />
                        </div>

                        {/* Avatar & Accent Color */}
                        <div className="flex items-center gap-6">
                            <div className="relative w-24 h-24 rounded-full overflow-hidden border-2 border-gray-700 group bg-gray-800 flex-shrink-0">
                                {avatarPreview ? (
                                    <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-500">
                                        <LucideIcons.User size={32} />
                                    </div>
                                )}
                                <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer" onClick={() => avatarInputRef.current.click()}>
                                    <LucideIcons.Camera size={24} className="text-white" />
                                </div>
                                <input 
                                    type="file" 
                                    ref={avatarInputRef} 
                                    onChange={handleAvatarChange} 
                                    className="hidden" 
                                    accept="image/png, image/jpeg, image/gif, image/webp" 
                                />
                            </div>

                            <div className="flex-1">
                                <label className="block text-sm text-gray-400 mb-1">{t('userProfile.accentColor')}</label>
                                <div className="flex items-center gap-3">
                                    <input 
                                        type="color" 
                                        value={accentColor} 
                                        onChange={(e) => setAccentColor(e.target.value)} 
                                        className="h-10 w-20 rounded bg-transparent cursor-pointer"
                                    />
                                    <span className="text-gray-300 font-mono text-sm">{accentColor}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="h-px bg-gray-800" />

                    {/* Information Section */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">{t('userProfile.edit.info') || "Information"}</h3>
                        
                        <div>
                            <label className="block text-sm text-gray-300 mb-1">{t('userProfile.displayName') || "Display Name"}</label>
                            <input 
                                type="text" 
                                value={displayName} 
                                onChange={(e) => setDisplayName(e.target.value)} 
                                className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                                placeholder={t('userProfile.displayNamePlaceholder') || "Your Name"}
                            />
                        </div>

                        <div>
                            <div className="flex justify-between items-center mb-1">
                                <label className="block text-sm text-gray-300">{t('userProfile.bio') || "Bio"}</label>
                                <div className="flex items-center gap-2">
                                    <select 
                                        value={selectedVibe} 
                                        onChange={(e) => setSelectedVibe(e.target.value)}
                                        className="bg-gray-800 border border-gray-600 text-xs text-gray-300 rounded p-1 focus:outline-none focus:border-teal-500"
                                    >
                                        {vibes.map(v => (
                                            <option key={v} value={v}>{v}</option>
                                        ))}
                                    </select>
                                    <button 
                                        onClick={confirmEnhanceBio} 
                                        disabled={isEnhancingBio || !bio} 
                                        className="text-teal-400 hover:text-teal-300 text-xs flex items-center gap-1 disabled:opacity-50"
                                    >
                                        {isEnhancingBio ? <LoadingSpinner size="xs" /> : <LucideIcons.Sparkles size={12} />}
                                        {t('userProfile.actions.enhanceWithAi') || "Enhance"}
                                    </button>
                                </div>
                            </div>
                            
                            {showBioSuggestions && (
                                <div className="mb-3 bg-gray-800 border border-teal-500/30 rounded-lg p-3 animate-fadeIn">
                                    <h4 className="text-xs font-bold text-teal-400 mb-2 uppercase">{t('userProfile.bioSuggestions') || "Suggestions"}</h4>
                                    <div className="space-y-2">
                                        {bioSuggestions.map((suggestion, idx) => (
                                            <button 
                                                key={idx}
                                                onClick={() => { setBio(suggestion); setShowBioSuggestions(false); }}
                                                className="w-full text-left p-2 bg-black/20 hover:bg-teal-500/10 rounded text-sm text-gray-200 transition-colors border border-transparent hover:border-teal-500/30"
                                            >
                                                "{suggestion}"
                                            </button>
                                        ))}
                                    </div>
                                    <button 
                                        onClick={() => setShowBioSuggestions(false)} 
                                        className="mt-2 text-xs text-gray-500 hover:text-white underline"
                                    >
                                        Dismiss
                                    </button>
                                </div>
                            )}

                            <textarea 
                                value={bio} 
                                onChange={(e) => setBio(e.target.value)} 
                                className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all min-h-[100px]"
                                placeholder={t('userProfile.bioPlaceholder') || "Tell us about yourself..."}
                            />
                        </div>

                        <div>
                            <label className="block text-sm text-gray-300 mb-1">{t('userProfile.interests') || "Interests"}</label>
                            <input 
                                type="text" 
                                value={interests} 
                                onChange={(e) => setInterests(e.target.value)} 
                                className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                                placeholder={t('userProfile.interestsPlaceholder') || "Coding, Design, Sci-Fi (comma separated)"}
                            />
                            <p className="text-xs text-gray-500 mt-1">{t('userProfile.interestsHelp') || "Separate with commas"}</p>
                        </div>
                    </div>

                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-800 bg-gray-800/50 flex justify-end gap-3">
                    <button 
                        onClick={onClose}
                        className="px-6 py-2 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700 transition-colors font-medium"
                        disabled={isSaving}
                    >
                        {t('userProfile.actions.cancel')}
                    </button>
                    <button 
                        onClick={handleSubmit}
                        disabled={isSaving}
                        className="px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold shadow-lg shadow-blue-900/20 transition-all flex items-center gap-2"
                    >
                        {isSaving ? <LoadingSpinner size="sm" color="white" /> : <LucideIcons.Save size={18} />}
                        {t('userProfile.actions.save')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditProfileModal;
